import { SCENE } from 'consts/scenes';

import type { StoreSet, StoreGet } from './index';

type SensorListItem = { id: number; Profile?: { profileType?: string } | null };

export type SearchQuerySlice = {
  timezone: string;
  activeSensors: number[];
  activeSensorsByProfile: number[] | undefined;
  lastSearch: Record<string, string>;
  setTimezone: (timezone: string) => void;
  setActiveSensors: (activeSensors: number[]) => void;
  setActiveSensorsByProfile: (sensorList: SensorListItem[] | null | undefined, pathname: string) => void;
  setLastSearch: (page: string, query: string) => void;
};

const filterActiveByAllowedProfiles = (
  sensorList: SensorListItem[] | null | undefined,
  activeSensors: number[],
  pathname: string,
): number[] | undefined => {
  if (!sensorList) {
    return undefined;
  }

  const sceneKey = (Object.keys(SCENE) as Array<keyof typeof SCENE>).find((key) => pathname.includes(SCENE[key].path));
  if (!sceneKey || !SCENE[sceneKey]) {
    return activeSensors;
  }

  const allowedProfileTypes = SCENE[sceneKey].profileTypes as readonly string[];
  return sensorList
    .filter((s) => allowedProfileTypes.includes(s?.Profile?.profileType as string) && activeSensors.includes(s.id))
    .map((s) => s.id);
};

const createSearchQuery = (set: StoreSet, get: StoreGet): SearchQuerySlice => ({
  timezone: localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone,
  activeSensors: [],
  activeSensorsByProfile: undefined,
  lastSearch: {}, // in-memory persistence of a last search query per dashboard
  setTimezone: (timezone) => {
    localStorage.setItem('timezone', timezone);
    set((prev) => ({
      searchQuery: {
        ...prev.searchQuery,
        timezone,
      },
    }));
  },
  setActiveSensors: (activeSensors) => {
    set((prev) => ({
      searchQuery: {
        ...prev.searchQuery,
        activeSensors: activeSensors.sort((a, b) => a - b),
      },
    }));
  },
  setActiveSensorsByProfile: (sensorList, pathname) => {
    const activeSensors = get().searchQuery.activeSensors;

    set((prev) => ({
      searchQuery: {
        ...prev.searchQuery,
        activeSensorsByProfile: filterActiveByAllowedProfiles(sensorList, activeSensors, pathname),
      },
    }));
  },
  setLastSearch: (page, query) => {
    set((prev) => ({
      searchQuery: {
        ...prev.searchQuery,
        lastSearch: {
          ...prev.searchQuery.lastSearch,
          [page]: query,
        },
      },
    }));
  },
});

export default createSearchQuery;
